import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DollarSign, Settings, Save, Filter } from 'lucide-react';
import api, { propertyAPI } from '../services/api';
import { toast } from 'sonner';

const OTA_COLORS = {
  booking: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  airbnb: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700' },
  expedia: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' },
  directo: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', badge: 'bg-green-100 text-green-700' }
};

const fmt = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function InformesOTAPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // OTA Commission settings
  const [commissions, setCommissions] = useState({
    booking_commission: 18,
    airbnb_commission: 15,
    expedia_commission: 20,
    iva_percentage: 19
  });
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    propertyAPI.getAll().then(res => {
      const props = res.data;
      setProperties(props);
      
    });
  }, []);

  const fetchCommissions = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const res = await api.get(`/ota-commissions/${selectedProperty}`);
      setCommissions(res.data);
    } catch (err) { console.error(err); }
  }, [selectedProperty]);

  const fetchReport = useCallback(async () => {
    if (!selectedProperty) return;
    setLoading(true);
    try {
      const params = [];
      if (fechaInicio) params.push(`fecha_inicio=${fechaInicio}`);
      if (fechaFin) params.push(`fecha_fin=${fechaFin}`);
      const res = await api.get(`/reports/${selectedProperty}${params.length ? '?' + params.join('&') : ''}`);
      setReportData(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [selectedProperty, fechaInicio, fechaFin]);

  useEffect(() => { fetchCommissions(); fetchReport(); }, [fetchCommissions, fetchReport]);

  const saveCommissions = async () => {
    try {
      await api.post('/ota-commissions', {
        property_id: selectedProperty,
        ...commissions
      });
      toast.success('Comisiones guardadas');
      setShowSettings(false);
    } catch (err) { toast.error('Error'); }
  };

  // Calculate OTA breakdown from reservations
  const calculateOTABreakdown = () => {
    if (!reportData?.reservations) return [];

    const otaMap = {};
    for (const res of reportData.reservations) {
      const source = (res.canal || res.source || res.fuente || 'directo').toLowerCase();
      let otaKey = 'directo';
      if (source.includes('booking')) otaKey = 'booking';
      else if (source.includes('airbnb')) otaKey = 'airbnb';
      else if (source.includes('expedia')) otaKey = 'expedia';

      if (!otaMap[otaKey]) {
        otaMap[otaKey] = { reservations: [], total: 0, count: 0, totalComision: 0, totalIVA: 0, totalNeto: 0 };
      }
      otaMap[otaKey].reservations.push(res);
      otaMap[otaKey].total += res.precio_total || 0;
      otaMap[otaKey].count += 1;
    }

    const commissionRates = {
      booking: (commissions.booking_commission || 18) / 100,
      airbnb: (commissions.airbnb_commission || 15) / 100,
      expedia: (commissions.expedia_commission || 20) / 100,
      directo: 0
    };

    // Calculate per-reservation: comision + IVA 19% solo nacionales (sobre base después de comisión)
    return Object.entries(otaMap).map(([ota, data]) => {
      const commRate = commissionRates[ota] || 0;
      let groupComision = 0;
      let groupIVA = 0;
      let groupNeto = 0;

      for (const res of data.reservations) {
        const valor = res.precio_total || 0;
        const comision = valor * commRate;
        const baseDespuesComision = valor - comision;
        const esNacional = (res.guest_nacionalidad || 'nacional') === 'nacional';
        const iva = esNacional ? baseDespuesComision * 0.19 : 0;
        const neto = baseDespuesComision - iva;
        groupComision += comision;
        groupIVA += iva;
        groupNeto += neto;
      }

      return {
        ota,
        name: ota === 'booking' ? 'Booking.com' : ota === 'airbnb' ? 'Airbnb' : ota === 'expedia' ? 'Expedia' : 'Reserva Directa',
        count: data.count,
        valorTotal: data.total,
        comisionPct: commRate * 100,
        comisionMonto: groupComision,
        totalIVA: groupIVA,
        neto: groupNeto,
        reservations: data.reservations
      };
    }).sort((a, b) => b.valorTotal - a.valorTotal);
  };

  const otaBreakdown = calculateOTABreakdown();
  const totalValor = otaBreakdown.reduce((a, b) => a + b.valorTotal, 0);
  const totalComision = otaBreakdown.reduce((a, b) => a + b.comisionMonto, 0);
  const totalIVA = otaBreakdown.reduce((a, b) => a + b.totalIVA, 0);
  const totalNeto = otaBreakdown.reduce((a, b) => a + b.neto, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="informes-ota-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>Ingresos por Reservas</h1>
            <p className="text-muted-foreground mt-1">Balance de ingresos por canal de venta con IVA para nacionales</p>
          </div>
          <div className="flex items-center gap-2">
            {properties.length > 0 && (
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="Propiedad" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="icon" onClick={() => setShowSettings(!showSettings)} data-testid="btn-commission-settings">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Commission Settings */}
        {showSettings && (
          <Card className="border-2 border-primary/20" data-testid="commission-settings">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Configurar Comisiones OTA (%)
              </CardTitle>
              <CardDescription>Estas comisiones se usarán para calcular el neto. IVA 19% se aplica automáticamente a nacionales.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Booking.com (%)</Label>
                  <Input type="number" value={commissions.booking_commission} onChange={e => setCommissions({ ...commissions, booking_commission: parseFloat(e.target.value) || 0 })} data-testid="input-booking-comm" />
                </div>
                <div>
                  <Label className="text-xs">Airbnb (%)</Label>
                  <Input type="number" value={commissions.airbnb_commission} onChange={e => setCommissions({ ...commissions, airbnb_commission: parseFloat(e.target.value) || 0 })} data-testid="input-airbnb-comm" />
                </div>
                <div>
                  <Label className="text-xs">Expedia (%)</Label>
                  <Input type="number" value={commissions.expedia_commission} onChange={e => setCommissions({ ...commissions, expedia_commission: parseFloat(e.target.value) || 0 })} data-testid="input-expedia-comm" />
                </div>
              </div>
              <Button className="mt-4" onClick={saveCommissions} data-testid="btn-save-commissions">
                <Save className="h-4 w-4 mr-2" /> Guardar Comisiones
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Date Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Período:</span>
              </div>
              <div>
                <Label className="text-xs">Desde</Label>
                <Input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className="w-40" />
              </div>
              <div>
                <Label className="text-xs">Hasta</Label>
                <Input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} className="w-40" />
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Cargando...</div>
        ) : (
          <>
            {/* Summary Totals */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-muted-foreground">Valor Total Reservas</p>
                  <p className="text-2xl font-bold" data-testid="total-valor">{fmt(totalValor)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-red-500">Total Comisiones OTA</p>
                  <p className="text-2xl font-bold text-red-600" data-testid="total-comision">{fmt(totalComision)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-xs text-orange-500">IVA 19% (Nacionales)</p>
                  <p className="text-2xl font-bold text-orange-600" data-testid="total-iva">{fmt(totalIVA)}</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-4">
                  <p className="text-xs text-green-600">Ingreso Neto</p>
                  <p className="text-2xl font-bold text-green-700" data-testid="total-neto">{fmt(totalNeto)}</p>
                </CardContent>
              </Card>
            </div>

            {/* OTA Breakdown Table */}
            <Card data-testid="ota-breakdown-table">
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Desglose por Canal de Venta</CardTitle>
              </CardHeader>
              <CardContent>
                {otaBreakdown.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">Sin reservas en el período seleccionado</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-3 px-3 font-semibold">Canal</th>
                          <th className="text-center py-3 px-3 font-semibold">Reservas</th>
                          <th className="text-right py-3 px-3 font-semibold">Valor Registrado</th>
                          <th className="text-right py-3 px-3 font-semibold">Comisión %</th>
                          <th className="text-right py-3 px-3 font-semibold text-red-600">Comisión $</th>
                          <th className="text-right py-3 px-3 font-semibold text-orange-600">IVA 19% Nac.</th>
                          <th className="text-right py-3 px-3 font-semibold text-green-600">Neto Recibido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {otaBreakdown.map(row => {
                          const colors = OTA_COLORS[row.ota] || OTA_COLORS.directo;
                          return (
                            <tr key={row.ota} className={`border-b ${colors.bg}`}>
                              <td className="py-3 px-3">
                                <Badge className={colors.badge}>{row.name}</Badge>
                              </td>
                              <td className="py-3 px-3 text-center font-medium">{row.count}</td>
                              <td className="py-3 px-3 text-right font-medium">{fmt(row.valorTotal)}</td>
                              <td className="py-3 px-3 text-right text-muted-foreground">{row.comisionPct}%</td>
                              <td className="py-3 px-3 text-right text-red-600 font-medium">{fmt(row.comisionMonto)}</td>
                              <td className="py-3 px-3 text-right text-orange-600 font-medium">{fmt(row.totalIVA)}</td>
                              <td className="py-3 px-3 text-right text-green-700 font-bold">{fmt(row.neto)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 bg-muted/80 font-bold">
                          <td className="py-3 px-3">TOTAL</td>
                          <td className="py-3 px-3 text-center">{otaBreakdown.reduce((a, b) => a + b.count, 0)}</td>
                          <td className="py-3 px-3 text-right">{fmt(totalValor)}</td>
                          <td className="py-3 px-3 text-right"></td>
                          <td className="py-3 px-3 text-right text-red-600">{fmt(totalComision)}</td>
                          <td className="py-3 px-3 text-right text-orange-600">{fmt(totalIVA)}</td>
                          <td className="py-3 px-3 text-right text-green-700">{fmt(totalNeto)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Individual Reservations */}
            <Card data-testid="reservations-detail">
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Detalle de Reservas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">Huésped</th>
                        <th className="text-left py-2 px-2">Check-in</th>
                        <th className="text-left py-2 px-2">Check-out</th>
                        <th className="text-left py-2 px-2">Canal</th>
                        <th className="text-center py-2 px-2">Nacional</th>
                        <th className="text-right py-2 px-2">Valor</th>
                        <th className="text-right py-2 px-2 text-red-600">Comisión</th>
                        <th className="text-right py-2 px-2 text-orange-600">IVA 19%</th>
                        <th className="text-right py-2 px-2 text-green-600">Neto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData?.reservations?.slice(0, 50).map((res, i) => {
                        const source = (res.canal || res.source || res.fuente || 'directo').toLowerCase();
                        let otaKey = 'directo';
                        if (source.includes('booking')) otaKey = 'booking';
                        else if (source.includes('airbnb')) otaKey = 'airbnb';
                        else if (source.includes('expedia')) otaKey = 'expedia';

                        const commRates = {
                          booking: (commissions.booking_commission || 18) / 100,
                          airbnb: (commissions.airbnb_commission || 15) / 100,
                          expedia: (commissions.expedia_commission || 20) / 100,
                          directo: 0
                        };
                        const commRate = commRates[otaKey] || 0;
                        const valor = res.precio_total || 0;
                        const comision = valor * commRate;
                        const baseDespuesComision = valor - comision;
                        const esNacional = (res.guest_nacionalidad || 'nacional') === 'nacional';
                        const iva = esNacional ? baseDespuesComision * 0.19 : 0;
                        const neto = baseDespuesComision - iva;
                        const colors = OTA_COLORS[otaKey] || OTA_COLORS.directo;

                        return (
                          <tr key={i} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-2">{res.guest_name} {res.guest_lastname || ''}</td>
                            <td className="py-2 px-2 text-xs">{res.fecha_checkin}</td>
                            <td className="py-2 px-2 text-xs">{res.fecha_checkout}</td>
                            <td className="py-2 px-2">
                              <Badge className={`text-xs ${colors.badge}`}>
                                {otaKey === 'booking' ? 'Booking' : otaKey === 'airbnb' ? 'Airbnb' : otaKey === 'expedia' ? 'Expedia' : 'Directo'}
                              </Badge>
                            </td>
                            <td className="py-2 px-2 text-center text-xs">{esNacional ? 'Sí' : 'No'}</td>
                            <td className="py-2 px-2 text-right font-medium">{fmt(valor)}</td>
                            <td className="py-2 px-2 text-right text-red-500">{fmt(comision)}</td>
                            <td className="py-2 px-2 text-right text-orange-500">{esNacional ? fmt(iva) : '-'}</td>
                            <td className="py-2 px-2 text-right text-green-600 font-bold">{fmt(neto)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 bg-muted/80 font-bold">
                        <td className="py-2 px-2" colSpan="5">TOTALES</td>
                        <td className="py-2 px-2 text-right">{fmt(totalValor)}</td>
                        <td className="py-2 px-2 text-right text-red-600">{fmt(totalComision)}</td>
                        <td className="py-2 px-2 text-right text-orange-600">{fmt(totalIVA)}</td>
                        <td className="py-2 px-2 text-right text-green-700">{fmt(totalNeto)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
